// src/components/Pentests/runs/PortScanView.js
//
// Port scan results: ports + services + versions + banners.
// Result rows are typically `result.value = "ip:port"` with data containing
// service, version, banner, state, protocol.

import { useMemo, useState } from 'react';
import { Network, Search } from 'lucide-react';

export default function PortScanView({ results }) {
  const [filter, setFilter] = useState('');

  const rows = useMemo(() => {
    return results.map((r) => {
      const d = r.data || {};
      const value = r.value || '';
      let ip = d.ip || d.host || '';
      let port = d.port;
      if (!ip || !port) {
        const m = value.match(/^(.*):(\d+)$/);
        if (m) {
          ip = ip || m[1];
          port = port || Number(m[2]);
        }
      }
      return {
        id: r.id,
        ip: ip || value,
        port: port || '',
        protocol: d.protocol || 'tcp',
        service: d.service || '',
        version: d.version || '',
        banner: d.banner || '',
        state: d.state || 'open',
      };
    });
  }, [results]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.ip, String(r.port), r.service, r.version, r.banner].some((s) =>
        String(s).toLowerCase().includes(q)
      )
    );
  }, [rows, filter]);

  if (results.length === 0) {
    return (
      <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-10 text-center text-slate-500">
        <Network className="w-6 h-6 mx-auto mb-2 opacity-60" />
        No open ports recorded yet.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by host, port, service, banner..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-cyber-100 border border-cyber-300/60 rounded-lg text-slate-900 placeholder-slate-500 outline-none focus:border-shinobi-green/70"
          />
        </div>
        <span className="text-xs text-slate-500 whitespace-nowrap">
          {filtered.length === rows.length
            ? `${rows.length} ports`
            : `${filtered.length} of ${rows.length}`}
        </span>
      </div>

      <div className="overflow-hidden rounded-2xl border border-cyber-300/40 bg-cyber-50/60">
        <table className="w-full text-sm text-left">
          <thead className="bg-cyber-200/60 text-xs uppercase tracking-wider text-slate-700">
            <tr>
              <th className="px-4 py-3">Host</th>
              <th className="px-4 py-3 text-right">Port</th>
              <th className="px-4 py-3">Proto</th>
              <th className="px-4 py-3">Service</th>
              <th className="px-4 py-3">Version</th>
              <th className="px-4 py-3">Banner</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-cyber-300/30 hover:bg-cyber-200/40">
                <td className="px-4 py-2 font-mono text-xs text-slate-800 break-all">{r.ip}</td>
                <td className="px-4 py-2 text-right tabular-nums text-shinobi-green font-mono">{r.port}</td>
                <td className="px-4 py-2 text-xs text-slate-500 uppercase">{r.protocol}</td>
                <td className="px-4 py-2 font-mono text-xs text-slate-800">{r.service || '-'}</td>
                <td className="px-4 py-2 font-mono text-xs text-slate-700">{r.version || '-'}</td>
                <td className="px-4 py-2 font-mono text-[11px] text-slate-500 break-all max-w-md">
                  {r.banner ? r.banner.slice(0, 200) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
